import React, { useEffect, useMemo, useState } from "react";
import { SLOTS } from "../lib/slots";
import {
  flattenFinanceEntries, fmtEUR, todayIso, fmtDateDE, fmtDateDEWeekday,
  PAYMENT_STATUS_OPTIONS, PAYMENT_STATUS_LABEL,
  PAYMENT_TYPE_OPTIONS, derivedDisplayStatus,
  loadFinanceFilters, saveFinanceFilters,
  financeYearStart, financeYearEnd, fmtRangeDE,
  loadRevenueGoal,
} from "../lib/finance";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { ChevronDown, ChevronUp, Filter, ChevronRight } from "lucide-react";
import { DateField } from "./DateField";

function defaultUntilIso()  { return financeYearEnd(); }
function defaultFromIso()   { return financeYearStart(); }

function StatusPill({ status }) {
  const color = {
    planned:  "bg-slate-100 text-slate-600",
    due:      "bg-amber-100 text-amber-800",
    invoiced: "bg-sky-100 text-sky-800",
    paid:     "bg-emerald-100 text-emerald-800",
  }[status] || "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tabular-nums tracking-wide ${color}`}>
      {PAYMENT_STATUS_LABEL[status] || status}
    </span>
  );
}

const UPCOMING_COLS = [
  { key: "invoice_date", label: "Rechnung",  width: "140px" },
  { key: "project_name", label: "Projekt",   width: "minmax(180px, 1.4fr)" },
  { key: "label",        label: "Zahlung",   width: "minmax(140px, 1fr)" },
  { key: "amount",       label: "Betrag",    width: "120px" },
  { key: "due_date",     label: "Fällig",    width: "140px" },
  { key: "status",       label: "Status",    width: "100px" },
];

function compareEntries(a, b, key) {
  if (key === "amount") return (Number(a.amount || 0)) - (Number(b.amount || 0));
  const av = a[key] ?? "";
  const bv = b[key] ?? "";
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}

export default function FinanceView({ projects, onSelectProject }) {
  const initial = useMemo(() => loadFinanceFilters(), []);
  const [fromDate, setFromDate] = useState(initial.fromDate);
  const [until, setUntil] = useState(initial.until);
  const [sortKey, setSortKey] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("asc");
  const [upcomingStatus, setUpcomingStatus] = useState(initial.upcomingStatus);
  const [upcomingTypes, setUpcomingTypes] = useState(initial.upcomingTypes);
  const [upcomingOpen, setUpcomingOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.sessionStorage.getItem("finance.upcoming.open");
    return v === null ? true : v === "1";
  });

  useEffect(() => {
    saveFinanceFilters({ fromDate, until, upcomingStatus, upcomingTypes });
  }, [fromDate, until, upcomingStatus, upcomingTypes]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("finance.upcoming.open", upcomingOpen ? "1" : "0");
    }
  }, [upcomingOpen]);

  const rawEntries = useMemo(() => flattenFinanceEntries(projects), [projects]);
  const today = todayIso();

  // Apply derived-display status (invoiced + due_date <= today → due).
  const entries = useMemo(
    () => rawEntries.map((e) => ({ ...e, status: derivedDisplayStatus(e, today) })),
    [rawEntries, today],
  );

  // Totals — all cards reflect the currently active filters (type, status, date range).
  // Each card additionally applies its own intrinsic status semantics, so e.g. selecting
  // status = "Berechnet" will zero out the "Noch zu fakturieren" card.
  const totals = useMemo(() => {
    const typeOK = (e) => upcomingTypes.length === 0 || upcomingTypes.includes(e.pay_type);
    const statusOK = (e) => {
      if (upcomingStatus === "all") return true;
      if (upcomingStatus === "open") return e.status === "invoiced" || e.status === "due";
      return e.status === upcomingStatus;
    };
    const inRange = (d) => (!fromDate || d >= fromDate) && (!until || d <= until);
    const dateOK = (e) => e.invoice_date && inRange(e.invoice_date);

    let toInvoice = 0, open = 0, overdue = 0, paid = 0;
    for (const e of entries) {
      const amt = Number(e.amount || 0);
      if (!amt) continue;
      if (!typeOK(e) || !statusOK(e) || !dateOK(e)) continue;

      // A) Noch zu fakturieren — planned entries.
      if (e.status === "planned") toInvoice += amt;

      // B) Offen — invoice issued, not paid, not planned.
      if (e.invoice_date && e.invoice_date <= today && e.status !== "paid" && e.status !== "planned") {
        open += amt;
      }

      // C) Überfällig — subset of Offen with due_date strictly in the past.
      if (
        e.invoice_date && e.invoice_date <= today &&
        e.status !== "paid" && e.status !== "planned" &&
        e.due_date && e.due_date < today
      ) {
        overdue += amt;
      }

      // D) Bezahlt — paid entries.
      if (e.status === "paid") paid += amt;
    }
    return { toInvoice, open, overdue, paid };
  }, [entries, upcomingTypes, upcomingStatus, fromDate, until, today]);

  // Upcoming: invoice_date inside the selected range and status != paid;
  // additional status/type filters apply on top. Sortable.
  const upcoming = useMemo(() => {
    let list = entries.filter(
      (e) =>
        e.invoice_date &&
        e.status !== "paid" &&
        (!fromDate || e.invoice_date >= fromDate) &&
        (!until || e.invoice_date <= until),
    );
    if (upcomingStatus === "open") {
      list = list.filter((e) => e.status === "invoiced" || e.status === "due");
    } else if (upcomingStatus !== "all") {
      list = list.filter((e) => e.status === upcomingStatus);
    }
    if (upcomingTypes.length > 0) list = list.filter((e) => upcomingTypes.includes(e.pay_type));
    list.sort((a, b) => {
      const r = compareEntries(a, b, sortKey);
      return sortDir === "asc" ? r : -r;
    });
    return list;
  }, [entries, sortKey, sortDir, upcomingStatus, upcomingTypes, fromDate, until]);

  const toggleType = (val) => {
    setUpcomingTypes((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  // Per-project grouping
  const perProject = useMemo(() => {
    const groups = new Map();
    for (const e of entries) {
      if (!groups.has(e.project_id)) groups.set(e.project_id, []);
      groups.get(e.project_id).push(e);
    }
    return Array.from(groups.entries()).map(([pid, items]) => {
      const proj = projects.find((p) => p.id === pid);
      const sum = items.reduce((acc, x) => acc + Number(x.amount || 0), 0);
      const paidSum = items.reduce((acc, x) => acc + (x.status === "paid" ? Number(x.amount || 0) : 0), 0);
      return { project: proj, items, sum, paidSum };
    });
  }, [entries, projects]);

  const gridTemplate = UPCOMING_COLS.map((c) => c.width).join(" ");

  return (
    <div className="flex-1 overflow-auto bg-slate-50" data-testid="finance-view">
      <div className="min-w-[920px] p-6 space-y-6">
        {/* Filter bar — own row so the date picker stays fully visible */}
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-6 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">
              Noch zu fakturieren — Zeitraum
            </div>
            <div className="flex items-center gap-2">
              <DateField
                value={fromDate}
                onChange={(iso) => setFromDate(iso || "")}
                className="w-[170px]"
                title="Von-Datum"
                testId="finance-from-input"
              />
              <span className="text-slate-400 text-sm">→</span>
              <DateField
                value={until}
                onChange={(iso) => setUntil(iso || "")}
                className="w-[170px]"
                title="Bis-Datum"
                testId="finance-until-input"
              />
              <button
                type="button"
                onClick={() => setFromDate(today)}
                className="h-9 px-2.5 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 tracking-wider uppercase whitespace-nowrap"
                title="Startdatum auf heute setzen"
                data-testid="finance-from-today-button"
              >
                Start heute
              </button>
              <button
                type="button"
                onClick={() => setUntil(today)}
                className="h-9 px-2.5 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 tracking-wider uppercase whitespace-nowrap"
                title="Enddatum auf heute setzen"
                data-testid="finance-until-today-button"
              >
                Bis heute
              </button>
              <button
                type="button"
                onClick={() => {
                  setFromDate(defaultFromIso());
                  setUntil(defaultUntilIso());
                }}
                className="h-9 px-2.5 rounded-md border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 tracking-wider uppercase whitespace-nowrap"
                title="Auf das aktuelle Kalenderjahr zurücksetzen"
                data-testid="finance-current-year-button"
              >
                Aktuelles Jahr
              </button>
              <button
                type="button"
                onClick={() => {
                  setFromDate("");
                  setUntil("");
                  setUpcomingTypes([]);
                  setUpcomingStatus("all");
                }}
                className="h-9 px-2.5 rounded-md border border-slate-300 hover:bg-slate-100 text-[11px] text-slate-700 tracking-wider uppercase whitespace-nowrap"
                title="Datumsbereich, Status- und Typ-Filter zurücksetzen"
                data-testid="finance-reset-filters-button"
              >
                Alle Filter zurücksetzen
              </button>
            </div>
          </div>
          <div className="text-xs text-slate-500 self-center">
            Heute: <span className="tabular-nums text-slate-700">{fmtDateDE(today)}</span>
          </div>
          {(() => {
            const jahresinkasso = totals.toInvoice + totals.open + totals.paid;
            const goal = loadRevenueGoal();
            const color = jahresinkasso >= goal ? "text-emerald-700" : "text-rose-700";
            return (
              <div
                className="text-xs text-slate-500 self-center"
                data-testid="finance-jahresinkasso"
                title={`Noch zu fakturieren + Offen + Bezahlt (Zeitraum ${fmtRangeDE(fromDate, until)})`}
              >
                Jahresinkasso im Zeitraum:{" "}
                <span className={`tabular-nums font-semibold ${color}`}>{fmtEUR(jahresinkasso)}</span>
              </div>
            );
          })()}
          <div className="text-xs text-slate-500 self-center ml-auto">
            {entries.length} Zahlungseintrag/-einträge insgesamt
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {(() => {
            const yearOfFrom = (fromDate || defaultFromIso()).slice(0, 4);
            const yearStart = `${yearOfFrom}-01-01`;
            const applyCardPreset = (statusValue) => {
              setFromDate(yearStart);
              setUntil(today);
              setUpcomingTypes([]);
              setUpcomingStatus(statusValue);
              setUpcomingOpen(true);
            };
            const cards = [
              { key: "toInvoice", title: `Noch zu fakturieren ${fmtRangeDE(fromDate, until)}`, value: totals.toInvoice, accent: "text-slate-900",  preset: "planned" },
              { key: "open",      title: "Offen",      value: totals.open,    accent: "text-sky-700",     preset: "open" },
              { key: "overdue",   title: "Überfällig", value: totals.overdue, accent: "text-rose-700",    preset: "due" },
              { key: "paid",      title: "Bezahlt",    value: totals.paid,    accent: "text-emerald-700", preset: null },
            ];
            return cards.map((c) => {
              const isActive = c.preset
                && upcomingStatus === c.preset
                && upcomingTypes.length === 0
                && fromDate === yearStart
                && until === today;
              const base = "text-left bg-white border rounded-md p-4 transition-colors";
              const interactive = c.preset
                ? " hover:bg-slate-50 hover:border-slate-300 cursor-pointer"
                : "";
              const activeStyle = isActive ? " border-slate-900 ring-1 ring-slate-900" : " border-slate-200";
              if (!c.preset) {
                return (
                  <div
                    key={c.key}
                    className={`${base}${activeStyle}`}
                    data-testid={`finance-total-${c.key}`}
                  >
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">{c.title}</div>
                    <div className={`text-2xl font-semibold tabular-nums ${c.accent}`}
                         style={{ fontFamily: "Manrope, ui-sans-serif" }}>
                      {fmtEUR(c.value)}
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => applyCardPreset(c.preset)}
                  className={`${base}${interactive}${activeStyle}`}
                  aria-pressed={isActive}
                  data-testid={`finance-total-${c.key}`}
                >
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">{c.title}</div>
                  <div className={`text-2xl font-semibold tabular-nums ${c.accent}`}
                       style={{ fontFamily: "Manrope, ui-sans-serif" }}>
                    {fmtEUR(c.value)}
                  </div>
                </button>
              );
            });
          })()}
        </div>

        {/* Upcoming Invoices */}
        <div className="bg-white border border-slate-200 rounded-md" data-testid="finance-upcoming">
          <div className={`px-4 py-3 flex items-baseline justify-between gap-3 flex-wrap ${upcomingOpen ? "border-b border-slate-200" : ""}`}>
            <button
              type="button"
              onClick={() => setUpcomingOpen((v) => !v)}
              className="text-sm font-medium text-slate-900 flex items-center gap-1.5 hover:text-slate-700 transition-colors"
              aria-expanded={upcomingOpen}
              data-testid="upcoming-collapse-toggle"
            >
              {upcomingOpen ? (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-500" />
              )}
              <span>Anstehende Rechnungen</span>
              {!upcomingOpen && (
                <span className="text-xs text-slate-400 tabular-nums ml-1">({upcoming.length})</span>
              )}
            </button>
            {upcomingOpen && (
              <div className="flex items-center gap-2">
                <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="h-8 inline-flex items-center gap-1.5 px-2.5 rounded-md border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="upcoming-type-filter-trigger"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    <span>
                      {upcomingTypes.length === 0
                        ? "Alle Typen"
                        : `${upcomingTypes.length} Typ${upcomingTypes.length === 1 ? "" : "en"}`}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-2">
                  <div className="flex items-baseline justify-between px-1 py-1">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">
                      Zahlungstyp
                    </div>
                    {upcomingTypes.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setUpcomingTypes([])}
                        className="text-[10px] text-slate-500 hover:text-slate-800"
                        data-testid="upcoming-type-filter-clear"
                      >
                        Zurücksetzen
                      </button>
                    )}
                  </div>
                  <div className="grid gap-1">
                    {PAYMENT_TYPE_OPTIONS.map((t) => {
                      const checked = upcomingTypes.includes(t.value);
                      return (
                        <label
                          key={t.value}
                          className="flex items-center gap-2 px-1 py-1 rounded-sm hover:bg-slate-50 cursor-pointer"
                          data-testid={`upcoming-type-option-${t.value}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleType(t.value)}
                          />
                          <span className="text-sm text-slate-700">{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              <Select value={upcomingStatus} onValueChange={setUpcomingStatus}>
                <SelectTrigger className="h-8 w-[140px]" data-testid="upcoming-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="open">Offen (Berechnet + Fällig)</SelectItem>
                  {PAYMENT_STATUS_OPTIONS.filter((s) => s.value !== "paid").map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 tabular-nums">{upcoming.length}</div>
            </div>
            )}
          </div>
          {upcomingOpen && (upcoming.length === 0 ? (
            <div className="px-4 py-6 text-xs text-slate-400">Keine offenen Rechnungen geplant.</div>
          ) : (
            <div>
              <div
                className="grid items-center px-4 py-2 text-[10px] tracking-[0.18em] uppercase text-slate-500 border-b border-slate-100"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                {UPCOMING_COLS.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className={`text-left flex items-center gap-1 hover:text-slate-900 ${
                      sortKey === c.key ? "text-slate-900" : ""
                    }`}
                    data-testid={`upcoming-sort-${c.key}`}
                  >
                    <span>{c.label}</span>
                    {sortKey === c.key && (
                      sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                ))}
              </div>
              {upcoming.map((e) => (
                <button
                  key={e.key}
                  onClick={() => onSelectProject?.(projects.find((p) => p.id === e.project_id))}
                  className="w-full grid items-center px-4 py-2 text-sm text-left hover:bg-slate-50 border-t border-slate-100 transition-colors"
                  style={{ gridTemplateColumns: gridTemplate }}
                  data-testid={`upcoming-row-${e.key}`}
                >
                  <div className={`text-xs tabular-nums ${
                    e.status === "planned" && e.invoice_date === today
                      ? "text-rose-600 font-semibold"
                      : "text-slate-700"
                  }`}>{fmtDateDEWeekday(e.invoice_date)}</div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-sm border border-black/10 shrink-0"
                        style={{ backgroundColor: SLOTS[e.slot - 1]?.color }}
                      />
                      {e.project_name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{e.client}</div>
                  </div>
                  <div className="text-xs text-slate-600 truncate">{e.label}</div>
                  <div className="text-xs tabular-nums text-slate-700">{fmtEUR(e.amount)}</div>
                  <div className="text-xs tabular-nums text-slate-500">{fmtDateDEWeekday(e.due_date) || "—"}</div>
                  <div><StatusPill status={e.status} /></div>
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Per Project */}
        <div className="bg-white border border-slate-200 rounded-md" data-testid="finance-per-project">
          <div className="px-4 py-3 border-b border-slate-200 flex items-baseline justify-between">
            <div className="text-sm font-medium text-slate-900">Pro Projekt</div>
            <div className="text-xs text-slate-500 tabular-nums">{perProject.length}</div>
          </div>
          {perProject.length === 0 ? (
            <div className="px-4 py-6 text-xs text-slate-400">
              Keine Zahlungsdaten erfasst. Öffne ein Projekt und ergänze Zahlungen.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {perProject.map(({ project, items, sum, paidSum }) => {
                if (!project) return null;
                return (
                  <div key={project.id} className="px-4 py-3">
                    <button
                      onClick={() => onSelectProject?.(project)}
                      className="w-full flex items-baseline justify-between text-left hover:opacity-80 transition-opacity"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-sm border border-black/10 shrink-0"
                          style={{ backgroundColor: SLOTS[project.slot - 1]?.color }}
                        />
                        <span className="text-sm font-medium text-slate-900 truncate">{project.name}</span>
                        <span className="text-xs text-slate-400">· {project.client}</span>
                      </div>
                      <div className="text-xs tabular-nums text-slate-500 shrink-0 ml-3">
                        <span className="text-emerald-700">{fmtEUR(paidSum)}</span>
                        <span className="text-slate-300 mx-1.5">/</span>
                        <span>{fmtEUR(sum)}</span>
                      </div>
                    </button>
                    <div className="mt-2 grid gap-1">
                      {items.map((e) => (
                        <div
                          key={e.key}
                          className="grid items-center text-xs"
                          style={{ gridTemplateColumns: "1fr 110px 110px 110px 90px" }}
                        >
                          <div className="text-slate-600 truncate">
                            {e.kind === "extra" && (
                              <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1.5">Zusatz</span>
                            )}
                            {e.label}
                          </div>
                          <div className="tabular-nums text-slate-700">{fmtEUR(e.amount)}</div>
                          <div className="tabular-nums text-slate-500">R: {fmtDateDE(e.invoice_date) || "—"}</div>
                          <div className="tabular-nums text-slate-500">F: {fmtDateDE(e.due_date) || "—"}</div>
                          <div><StatusPill status={e.status} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
